/**
 * Copyright (c) 2025-2099 GitCoffee All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { search } from '@gitcoffee/api';
import { getItem, removeItem, setItem } from '@gitcoffee/storage';
import { defineStore } from '@gitcoffee/store';
import { APP_SETTING } from '../config/config';

export interface UserInfo {
  nickname: string;
  credits: number;
}

export interface AppSettings {
  language: string;
  theme: string;
  trustedDomains: string[];
  smartSearch: boolean;
}

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    // 用户信息
    userInfo: {
      nickname: '',
      credits: 0,
    } as UserInfo,
    // 应用设置
    appSettings: {
      language: 'zh',
      theme: 'light',
      trustedDomains: [],
      smartSearch: APP_SETTING.smartSearch,
    } as AppSettings,
    // 加载状态
    isLoading: false,
    isLoggedIn: false,
  }),

  getters: {
    // 计算用户是否登录
    getIsLoggedIn: (state) => !!state.userInfo.nickname,
    // 获取用户信息
    getUserInfo: (state) => state.userInfo,
    // 获取应用设置
    getAppSettings: (state) => state.appSettings,
    // 获取受信任域名
    getTrustedDomains: (state) => state.appSettings.trustedDomains,
  },

  actions: {
    // 初始化设置
    async initialize() {
      this.isLoading = true;
      try {
        // 加载用户信息
        await this.loadUserInfo();
        // 加载应用设置
        await this.loadAppSettings();
      } catch (error) {
        console.error('Failed to initialize settings:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // 加载用户信息
    async loadUserInfo() {
      try {
        // 从API获取用户信息
        const res = await search.user.userInfoApi({});
        if (res && res.data) {
          const data = res.data;
          this.userInfo = data;
          this.isLoggedIn = !!data.nickname;
          // 保存到本地存储
          await setItem('user', JSON.stringify(data));
          return data;
        }
      } catch (error) {
        console.error('Failed to load user info:', error);
      }
      // 尝试从本地存储加载（API调用失败或返回undefined时）
      const savedUser = await getItem('user');
      if (savedUser) {
        this.userInfo = JSON.parse(savedUser);
        this.isLoggedIn = !!this.userInfo.nickname;
      }
      return null;
    },

    // 保存用户信息
    async saveUserInfo(userData: UserInfo) {
      this.userInfo = userData;
      this.isLoggedIn = !!userData.nickname;
      await setItem('user', JSON.stringify(userData));
    },

    // 登录
    login() {
      window.open(
        'https://searchfly.exmay.com/exmay/searchfly/center/home',
        '_blank'
      );
    },

    // 登出
    async logout() {
      try {
        await search.account.logoutApi({});
        // 清除用户信息
        this.userInfo = {
          nickname: '',
          credits: 0,
        };
        this.isLoggedIn = false;
        // 移除本地存储
        await removeItem('user');
        return true;
      } catch (error) {
        console.error('Failed to logout:', error);
        return false;
      }
    },

    // 加载应用设置
    async loadAppSettings() {
      try {
        // 从本地存储加载应用设置，使用不同的存储键以避免与@gitcoffee/app包冲突
        const savedSettings = await getItem('searchflySettings');
        if (savedSettings) {
          this.appSettings = {
            ...this.appSettings,
            ...JSON.parse(savedSettings),
          };
        }
      } catch (error) {
        console.error('Failed to load app settings:', error);
      }
    },

    // 保存应用设置
    async saveAppSettings(settings: Partial<AppSettings>) {
      this.appSettings = { ...this.appSettings, ...settings };
      // 使用不同的存储键以避免与@gitcoffee/app包冲突
      await setItem('searchflySettings', JSON.stringify(this.appSettings));
    },

    // 保存受信任域名
    async saveTrustedDomains(domains: string[]) {
      this.appSettings.trustedDomains = domains;
      await this.saveAppSettings(this.appSettings);
    },

    // 切换语言
    async changeLanguage(language: string) {
      await this.saveAppSettings({ language });
    },

    // 切换主题
    async changeTheme(theme: string) {
      await this.saveAppSettings({ theme });
    },
  },
});
